import { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Badge,
  Card,
  Table,
  Text,
  Loader,
  Alert,
  Pagination,
  SegmentedControl,
  ActionIcon,
  Tooltip,
  Modal,
  Textarea,
  Button,
  Stack,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconExclamationCircle,
  IconRefresh,
  IconFlag,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApiClient, useLiveQuery } from '../../api';

const PAGE_LIMIT = 20;

const REASON_LABELS = {
  spam: 'Spam',
  harassment: 'Harassment',
  scam: 'Scam',
  inappropriate_content: 'Inappropriate content',
  other: 'Other',
};

const STATUS_COLORS = {
  open: 'yellow',
  resolved: 'green',
  dismissed: 'gray',
};

// Admin reports queue (contract §4.14): list user reports, resolve/dismiss
// open ones with optional admin notes. Kept live on the admin.reports topic
// (report.created / report.resolved).
const ReportsQueue = () => {
  const api = useApiClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  // { report, status: 'resolved' | 'dismissed' } while the confirm modal is open
  const [pendingAction, setPendingAction] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useLiveQuery(
    '/api/v1/admin/reports',
    {
      page,
      limit: PAGE_LIMIT,
      status: statusFilter === 'all' ? undefined : statusFilter,
    },
    { topics: ['admin.reports'] }
  );

  const reports = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.limit ?? PAGE_LIMIT)));

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const openAction = (report, status) => {
    setPendingAction({ report, status });
    setAdminNotes('');
  };

  const confirmAction = async () => {
    if (!pendingAction || saving) return;
    setSaving(true);
    try {
      const body = { status: pendingAction.status };
      const notes = adminNotes.trim();
      if (notes) body.admin_notes = notes;
      await api.patch(`/api/v1/admin/reports/${pendingAction.report.id}`, body);
      notifications.show({
        title: 'Report updated',
        message: `Report #${pendingAction.report.id} ${pendingAction.status}`,
        color: 'green',
        icon: <IconCheck size="1rem" />,
      });
      setPendingAction(null);
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Update failed',
        message: err?.message || 'Could not update report',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && reports.length === 0) {
    return (
      <Container size="xl" py="md">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group align="center" mb="lg">
        <IconFlag size="1.5rem" color="var(--mantine-color-red-6)" />
        <Title order={1} size="h2">Reports</Title>
        <Badge color="pink" variant="light">Admin</Badge>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Group justify="space-between">
          <SegmentedControl
            value={statusFilter}
            onChange={handleFilterChange}
            data={[
              { value: 'all', label: 'All' },
              { value: 'open', label: 'Open' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'dismissed', label: 'Dismissed' },
            ]}
          />
          <ActionIcon onClick={refetch} variant="light" aria-label="Refresh">
            <IconRefresh size="1rem" />
          </ActionIcon>
        </Group>
        {error && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red" mt="md">
            {error.message}
          </Alert>
        )}
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Reporter</Table.Th>
              <Table.Th>Reported</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Details</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {reports.map((report) => (
              <Table.Tr key={report.id}>
                <Table.Td>
                  {report.admin_notes ? (
                    <Tooltip label={report.admin_notes} multiline maw={320} withArrow>
                      <Badge color={STATUS_COLORS[report.status] || 'gray'} variant="light">
                        {report.status}
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Badge color={STATUS_COLORS[report.status] || 'gray'} variant="light">
                      {report.status}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>{report.reporter?.display_name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>{report.reported?.display_name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{REASON_LABELS[report.reason] || report.reason}</Text>
                </Table.Td>
                <Table.Td>
                  {report.details ? (
                    <Tooltip label={report.details} multiline maw={320} withArrow>
                      <Text size="sm" truncate="end" maw={220}>
                        {report.details}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text size="sm" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td>{formatDate(report.created_at)}</Table.Td>
                <Table.Td>
                  {report.status === 'open' ? (
                    <Group gap="xs" wrap="nowrap">
                      <ActionIcon
                        variant="light"
                        color="green"
                        onClick={() => openAction(report, 'resolved')}
                        aria-label="Resolve"
                        title="Resolve"
                      >
                        <IconCheck size="1rem" />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="gray"
                        onClick={() => openAction(report, 'dismissed')}
                        aria-label="Dismiss"
                        title="Dismiss"
                      >
                        <IconX size="1rem" />
                      </ActionIcon>
                    </Group>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {report.resolved_at ? formatDate(report.resolved_at) : '—'}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {reports.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No reports found</Text>
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Card>

      {/* Resolve/Dismiss confirmation modal */}
      <Modal
        opened={!!pendingAction}
        onClose={() => setPendingAction(null)}
        title={
          pendingAction
            ? `${pendingAction.status === 'resolved' ? 'Resolve' : 'Dismiss'} report #${pendingAction.report.id}`
            : ''
        }
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Textarea
            label="Admin notes"
            placeholder="Optional notes about this decision"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.currentTarget.value)}
            maxLength={2000}
            autosize
            minRows={3}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              color="gray"
              onClick={() => setPendingAction(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color={pendingAction?.status === 'resolved' ? 'green' : 'gray'}
              onClick={confirmAction}
              loading={saving}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ReportsQueue;
